import { useState, useRef, useCallback } from 'react'
import { useSnapshot } from 'valtio'
import { state } from '../store'
import '../styles.css'

export default function Overlay() {
  const { colors, selectedColor } = useSnapshot(state)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef(null)

  const toggleMusic = useCallback(() => {
    if (!audioRef.current) return
    if (!isPlaying) {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(console.error)
    } else {
      audioRef.current.pause()
      setIsPlaying(false)
    }
  }, [isPlaying])

  const selectColor = useCallback((color) => {
    state.selectedColor = color
  }, [])

  // Smooth scroll to section
  const scrollToSection = useCallback((sectionNum) => {
    const section = document.querySelector(`.section-${sectionNum}`)
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  return (
    <div className="container">
      <header>
        <div className="brand">
          <img src="/assets/images/logo.svg" alt="Razer Logo" width="45" height="35" />
          <p>RAZER <strong>BASILISK V3</strong></p>
        </div>

        <nav className="header-nav">
          <ul>
            <li onClick={() => scrollToSection(1)}>Home</li>
            <li onClick={() => scrollToSection(3)}>Products</li>
            <li onClick={() => scrollToSection(5)}>Services</li>
            <li onClick={() => scrollToSection(7)}>Contact</li>
          </ul>
        </nav>

        <button onClick={toggleMusic} aria-pressed={isPlaying}>
          {isPlaying ? 'PAUSE' : 'SOUND'}
        </button>
        <audio ref={audioRef} src="/assets/audio/music.mp3" preload="auto" loop />
      </header>

      <main className="main-wrapper">
        <section className="section section-1">
          <div className="section-1-wrapper">
            <h1 className="section-1-title">Razer Basilisk V3</h1>
            <h2 className="section-1-title-info">Razer's High Performance Wireless Gaming Mouse</h2>
            <p className="section-1-text">
              Fully configurable buttons for quick access to macros and secondary functions.
              Tailored control to suit any playstyle, from MMO to FPS gaming.
            </p>
            <p className="section-1-price">USD 99.99</p>
            <div className="section-1-buttons">
              <button onClick={() => scrollToSection(6)}>ADD TO CART</button>
              <button onClick={() => scrollToSection(3)}>MORE DETAILS</button>
            </div>
          </div>
        </section>

        <section className="section section-2">
          <div className="section-2-wrapper">
            <h1>Razer V3</h1>
            <div className="section-2-info">
              <h2>Customizable RGB Lighting</h2>
              <p>
                Experience the iconic Razer Chroma RGB with 11 lighting zones and dynamic
                effects that sync across devices. Ergonomically designed for long gaming
                sessions with unmatched comfort.
              </p>
            </div>
          </div>
        </section>

        <section className="section section-3">
          <div className="section-3-wrapper">
            <div className="section-3-row">
              <article className="section-3-card">
                <video src="/assets/videos/video-1.mp4" autoPlay loop muted playsInline />
                <h2>TACTILE CYCLING MODE</h2>
                <p>For high-precision and distinct notched scrolling ideal for cycling through weapons or skills.</p>
              </article>
              <article className="section-3-card">
                <video src="/assets/videos/video-2.mp4" autoPlay loop muted playsInline />
                <h2>SMART-REEL MODE</h2>
                <p>Enabled via Razer Synapse, the scroll wheel automatically shifts from tactile to free-spin mode when you scroll faster.</p>
              </article>
            </div>
            <div className="section-3-row">
              <article className="section-3-card">
                <video src="/assets/videos/video-3.mp4" autoPlay loop muted playsInline />
                <h2>FREE-SPIN SCROLLING MODE</h2>
                <p>For smooth, high-speed scrolling perfect for covering content quickly or emulating repeated game commands.</p>
              </article>
              <article className="section-3-card">
                <video src="/assets/videos/video-4.mp4" autoPlay loop muted playsInline />
                <h2>VIRTUAL ACCELERATION</h2>
                <p>By turning on this Razer Synapse feature, you'll be able to increase the mouse's scroll speed the faster you scroll.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="section section-4">
          <div className="section-4-wrapper">
            <div className="section-4-info-wrapper">
              <h2>Razer HyperScroll <br /> Tilt Wheel</h2>
              <p>Seamlessly switch between free-spin scrolling or tactile cycling, allowing precise navigation during gameplay and productivity tasks.</p>
            </div>
            <img src="/assets/images/mouse-info.jpg" alt="Razer Basilisk V3" width="500" height="320" loading="lazy" />
          </div>
        </section>

        <section className="section section-5">
          <div className="section-5-wrapper">
            <div className="bike-power">
              <div className="bike-power-stats">
                <p><strong>26K DPI</strong><br /> SENSOR</p>
                <p><strong>75g</strong><br /> WEIGHT</p>
                <p><strong>8</strong><br /> BUTTONS</p>
                <p><strong>Razer Chroma</strong><br /> RGB</p>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-6">
          <div className="section-6-wrapper">
            <div className="section-6-info-wrapper">
              <h2>Color</h2>
              <p>Choose your favorite color:</p>
            </div>
            <div className="section-6-colors-wrapper">
              <ul>
                {colors.map((color) => (
                  <li
                    key={color}
                    style={{ backgroundColor: color }}
                    className={selectedColor === color ? 'selected' : ''}
                    onClick={() => selectColor(color)}
                  />
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="section section-7">
          <div className="section-7-wrapper">
            <img src="/assets/images/Basilisk-V3.webp" alt="Razer Basilisk V3" width="800" height="550" loading="lazy" />
          </div>
        </section>
      </main>

      <footer>
        <div className="footer-left">
          <div className="footer-info-wrapper">
            <ul>
              <h3>About:</h3>
              <li>Home</li>
              <li>Store</li>
              <li>About Us</li>
              <li>Contacts</li>
            </ul>
            <ul>
              <h3>Legal:</h3>
              <li>News</li>
              <li>Terms and Conditions</li>
              <li>Privacy Policy</li>
              <li>Cookie Policy</li>
            </ul>
          </div>
          <div className="footer-h2">
            <img src="/assets/images/logo.svg" alt="Razer Logo" width="50" height="50" />
            <h2>Razer Basilisk V3</h2>
          </div>
          <p className="copyright">All rights reserved 2025 by Unfor Dev</p>
        </div>
      </footer>
    </div>
  )
}
